/**
 * 2026-10-21 (owner) · a CLEANING step's settings (services/run/cleaning.py).
 *
 * "Hame bas abhi ek step type add karna hai, usme operator manually fill karega cleaning ka." The
 * author chooses the type of cleaning — it decides the steps the operator ticks and whether a
 * detergent and rinse are demanded — and which attachments are required. The form itself is the
 * same on every cleaning step and is frozen into the step when a batch is issued; it is served with
 * the step (`cleaning_step.forms`, or the vocabulary's `cleaning` for a step not yet saved) and drawn
 * here as the operator's form — "it should look like a form" — so the author reads the form itself.
 * The area and the equipment are not asked (the area is chosen when the step starts, the equipment
 * is the step's own).
 */
import React, { useRef } from "react";
import { Button, Checkbox, DatePicker, Input, InputNumber, Radio, Select, Switch, Tag, Typography } from "antd";
import { CalendarOutlined, CameraOutlined, LockOutlined, PaperClipOutlined, UploadOutlined } from "@ant-design/icons";
import { Field } from "./Field";

const TYPES = [
  { value: "A", label: "Type A — minor cleaning (batch to batch, same product)" },
  { value: "B", label: "Type B — major cleaning (product change-over)" },
];

const WHAT = {
  A: "Remove the previous material, dry-clean, wipe, label. Detergent and rinse are optional.",
  B: "Dismantle, dry-clean, wash with detergent, rinse, dry, re-assemble, label. Detergent, concentration and rinse are required.",
};

const muted = { color: "var(--dz-faint, #64748b)", fontSize: 12.5, fontWeight: 400 };
const req = { color: "#dc2626" };
const card = { border: "1px solid var(--dz-line, #dbe3ea)", borderRadius: 10, padding: 14, marginTop: 8,
               background: "var(--dz-surface-2, #f8fafc)", width: "100%" };
const cardHead = { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap",
                   fontWeight: 700, fontSize: 14, marginBottom: 12 };
const section = { fontSize: 12.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase",
                  color: "var(--dz-faint, #64748b)", margin: "4px 0 8px" };
const label = { fontSize: 14, fontWeight: 600, marginBottom: 4 };
const grid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 };
const tile = { display: "flex", gap: 8, alignItems: "flex-start", border: "1px solid var(--dz-line, #dbe3ea)",
               borderRadius: 8, padding: "8px 10px", background: "#fff", fontSize: 13.5 };
const full = { width: "100%" };

/** The input the operator gets for a field — the same kind of control, disabled. */
function inputFor(f) {
  if (f.locked) return <Input disabled value={f.value} prefix={<LockOutlined />} style={full} />;
  if ((f.options || []).length) {
    // Openable, so the author can see what the operator chooses from; nothing chosen here is kept.
    return (
      <>
        <Select style={full} placeholder="Choose…  (open to see the choices)" value={null} onChange={() => {}}
                options={f.options.map((o) => ({ value: o, label: o }))} />
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
          <span style={muted}>Choices ({f.options.length}):</span>
          {f.options.map((o) => <Tag key={o} style={{ marginInlineEnd: 0 }}>{o}</Tag>)}
        </div>
      </>
    );
  }
  switch (f.data_type) {
    // 2026-10-22 (owner) · "Equipment cleaned": the machines standing in the step's area, chosen many at once.
    case "CHECKBOX":
      return <Select disabled mode="multiple" style={full} value={[]}
                     placeholder="Select the equipment cleaned — the machines in this step's area" />;
    case "DATETIME":
    case "DATE":
      return <DatePicker disabled showTime style={full} placeholder="Date and time  ·  or Now" suffixIcon={<CalendarOutlined />} />;
    case "FLOAT":
    case "INTEGER":
      return <InputNumber disabled style={full} placeholder={f.data_type === "INTEGER" ? "0" : "0.00"} addonAfter={f.unit || undefined} />;
    case "BOOLEAN":
      return <Radio.Group disabled options={["Yes", "No"]} optionType="button" />;
    default:
      return <Input disabled style={full} placeholder="Enter value" />;
  }
}

/** An attachment as the tablet draws it: what it is, take a photo or upload one, and what is accepted. */
function AttachBox({ what, words, note }) {
  return (
    <div style={{ ...tile, alignItems: "center", marginTop: 8, borderStyle: "dashed", flexWrap: "wrap" }}>
      <PaperClipOutlined style={{ fontSize: 16, color: "var(--dz-accent, #204448)" }} />
      <span style={{ flex: 1, minWidth: 160 }}>
        <div style={{ fontWeight: 600 }}>{what}{note ? <span style={muted}> · {note}</span> : null}</div>
        <div style={muted}>Accepted: {words}</div>
      </span>
      <Button size="small" disabled icon={<CameraOutlined />}>Photo</Button>
      <Button size="small" disabled icon={<UploadOutlined />}>Upload</Button>
    </div>
  );
}

export function CleaningField({ step, readOnly, onChange, vocab }) {
  const cfg = step?.cleaning_step || {};
  // The form and steps are served with the step; a draft change (the type) carries only the settings,
  // so the last served preview is kept to draw the chosen type's form until the step is saved — and a
  // step not yet saved (or just re-typed to Cleaning) is drawn from the served vocabulary.
  const served = useRef(null);
  if (cfg.forms) served.current = cfg;
  const preview = served.current || vocab?.cleaning || {};
  const type = cfg.type || "B";
  const attach = !!cfg.attach_required;
  const sampleAttach = !!cfg.sample_attach;
  // 2026-10-21 (owner) · "sare fields ke liye only required rakho": each field is on (asked, required)
  // or off (not asked at all). Unset, a type's own defaults; a change of type starts from its defaults.
  const defaults = (preview.defaults || {})[type] || [];
  const chosen = Array.isArray(cfg.fields) ? cfg.fields : defaults;
  const save = (patch) => onChange({ cleaning_step: { type, attach_required: attach, sample_attach: sampleAttach,
                                                      fields: chosen, ...patch } });
  const toggle = (labelOf, on) => save({ fields: on ? [...chosen.filter((x) => x !== labelOf), labelOf] : chosen.filter((x) => x !== labelOf) });
  const candidates = preview.candidates || [];
  const sampleOn = chosen.includes("Swab / rinse sample taken");
  const form = candidates.length
    ? [{ label: "Cleaning type", data_type: "TEXT", locked: true, value: TYPES.find((t) => t.value === type)?.label.split(" (")[0] },
       ...candidates.filter((f) => f.label !== "Sample ID" || sampleOn)]
    : (preview.forms || {})[type] || [];
  const steps = (preview.steps || {})[type] || [];
  const att = preview.attachments || {};
  const accepted = (preview.accepted || {}).words || "JPG, PNG, PDF · up to 10 MB";
  return (
    <>
      <Field label="Type of cleaning" hint={WHAT[type]}>
        <Select size="small" style={{ width: "100%", maxWidth: 460 }} value={type} disabled={readOnly}
                options={TYPES} onChange={(v) => save({ type: v, fields: (preview.defaults || {})[v] || chosen })} />
      </Field>
      {/* 2026-10-21 (owner) · "samajh nahi aa raha kya hai — image upload karna hai, ye dikhna chahiye":
          named for what the operator does — a photo or a file of the paper record — with the mark of it. */}
      <Field label={<span><CameraOutlined /> Photo / file of the cleaning record</span>}
             hint="The filled paper cleaning record, photographed on the tablet or uploaded as a file.">
        <div>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Switch checked={attach} disabled={readOnly} onChange={(v) => save({ attach_required: v })} />
            <Tag color={attach ? "red" : undefined}>{attach ? "Required" : "Not asked"}</Tag>
          </span>
          <div style={{ ...muted, marginTop: 4 }}>
            {attach ? "The operator must take a photo of the filled cleaning record, or upload it" : "The operator is not asked for a photo of the cleaning record"}
            {" "}· {accepted}
          </div>
        </div>
      </Field>
      {/* 2026-10-21 (owner) · on: a swab / rinse sample taken is attached too — a photo taken on the
          tablet or a file uploaded (its label or report), beside its sample ID. */}
      <Field label={<span><CameraOutlined /> Photo / file of the swab / rinse sample</span>}
             hint="The sample's label or its report, photographed on the tablet or uploaded, beside its sample ID.">
        <div>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Switch checked={sampleAttach} disabled={readOnly} onChange={(v) => save({ sample_attach: v })} />
            <Tag color={sampleAttach ? "blue" : undefined}>{sampleAttach ? "Required if sampled" : "Not asked"}</Tag>
          </span>
          <div style={{ ...muted, marginTop: 4 }}>
            {sampleAttach ? "When a swab / rinse sample is taken, the operator must take a photo of its label or report, or upload it"
                          : "A sample taken needs only its sample ID — no photo"}
            {" "}· {accepted}
          </div>
        </div>
      </Field>

      {/* The operator's cleaning form, drawn as the tablet draws it — the checklist, each value in its
          own input, then the attachment — disabled here. */}
      {form.length ? (
        <div data-testid="cleaning-form-preview" style={card}>
          <div style={cardHead}>
            <span>Operator form — preview</span>
            <span style={muted}>Area and equipment come from the step</span>
          </div>

          {steps.length ? (
            <div style={{ marginBottom: 14 }}>
              <div style={section}>Checklist · {steps.length} to tick</div>
              <div style={grid}>
                {steps.map((st) => (
                  <div key={st} style={tile}>
                    <Checkbox disabled />
                    <span>{st}<span style={req}> *</span></span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div style={section}>Recorded values</div>
          {form.map((f) => {
            const sampleId = f.label === "Sample ID";
            const switchable = !f.locked && !sampleId;
            const on = !switchable || chosen.includes(f.label);
            return (
              <div key={f.label} style={{ marginBottom: 12, opacity: on ? 1 : 0.45 }}>
                <div style={{ ...label, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ flex: 1 }}>
                    {f.label}{on && switchable ? <span style={req}> *</span> : null}
                    {f.locked ? <span style={muted}> · from the MBMR, locked</span> : null}
                    {sampleId ? <span style={muted}> · required when a sample is taken</span> : null}
                    {!on ? <span style={muted}> · not asked</span> : null}
                  </span>
                  {switchable ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, opacity: 1 }}>
                      <Switch size="small" checked={on} disabled={readOnly} onChange={(v) => toggle(f.label, v)} />
                      <span style={{ ...muted, minWidth: 58 }}>{on ? "Required" : "Off"}</span>
                    </span>
                  ) : null}
                </div>
                {on ? inputFor(f) : null}
                {sampleId && sampleAttach ? (
                  <AttachBox what={att.sample || "Attach the swab / rinse sample label or report"} words={accepted}
                             note="required when a sample is taken" />
                ) : null}
              </div>
            );
          })}

          {attach ? (
            <div style={{ marginTop: 4 }}>
              <div style={section}>Document the record asks for</div>
              <AttachBox what={att.record || "Attach the completed cleaning record"} words={accepted} note="required" />
            </div>
          ) : null}
        </div>
      ) : (
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          The cleaning form could not be loaded — reload the page.
        </Typography.Text>
      )}
    </>
  );
}
